import React, { useMemo } from 'react';
import Editor from 'react-simple-code-editor';
import styles from './TemplateEditor.module.css';

export type PrefixHighlight = {
	/** Prefix to match, e.g. "$env" */
	prefix: string;
	/** Color to style the prefix keyword span */
	color: string;
	/**
	 * When true, highlight object[property] patterns
	 */
	allowBrackets?: boolean;
	/**
	 * When true, highlight the prefix by itself without requiring dot or bracket patterns
	 */
	allowStandalone?: boolean;
};

type TemplateEditorProps = {
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	disabled?: boolean;
	className?: string;
	padding?: number;
	/**
	 * When true, highlight $vars/$secrets patterns even outside mustache brackets
	 */
	highlightVariablesOutsideBraces?: boolean;
	/**
	 * Optional list of custom prefixes to highlight in the same style as variables
	 * Example: [{ prefix: '$env', color: '#9cdcfe' }]
	 */
	customPrefixes?: PrefixHighlight[];
	/**
	 * Callback fired when the editor loses focus
	 */
	onBlur?: () => void;
};

/**
 * Highlights variable patterns within template content
 * @param content - The content inside mustache brackets
 * @returns HTML string with highlighted variable patterns
 */
const escapeForRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const highlightVariables = (content: string, customPrefixes?: PrefixHighlight[]): string => {
	let result = content
		// Highlight $vars.property pattern
		.replace(/(\$vars)(\.)(\w+)/g, '<span class="var-keyword">$1</span><span class="var-dot">$2</span><span class="var-property">$3</span>')
		// Highlight $vars[property] pattern
		.replace(/(\$vars)(\[)([^\]]+)(\])/g, '<span class="var-keyword">$1</span><span class="var-bracket">$2</span><span class="var-property">$3</span><span class="var-bracket">$4</span>')
		// Highlight $secrets.property pattern
		.replace(/(\$secrets)(\.)(\w+)/g, '<span class="var-keyword">$1</span><span class="var-dot">$2</span><span class="var-property">$3</span>');

	if (customPrefixes && customPrefixes.length > 0) {
		for (const { prefix, color, allowBrackets, allowStandalone } of customPrefixes) {
			if (!prefix) { continue; }
			const escaped = escapeForRegExp(prefix);
			// prefix.property
			const dotPattern = new RegExp(`(${escaped})(\\.)(\\w+)`, 'g');
			result = result.replace(
				dotPattern,
				`<span class="var-keyword" style="color: ${color}">$1</span><span class="var-dot">$2</span><span class=\"var-property\">$3</span>`
			);
			// prefix[anything]
			if (allowBrackets) {
				const bracketPattern = new RegExp(`(${escaped})(\\[)([^\\]]+)(\\])`, 'g');
				result = result.replace(
					bracketPattern,
					`<span class="var-keyword" style="color: ${color}">$1</span><span class="var-bracket">$2</span><span class="var-property">$3</span><span class="var-bracket">$4</span>`
				);
			}
			// prefix by itself (not followed by dot or bracket)
			if (allowStandalone) {
				const standalonePattern = new RegExp(`(${escaped})(?![.\\[])`, 'g');
				result = result.replace(
					standalonePattern,
					`<span class="var-keyword" style="color: ${color}">$1</span>`
				);
			}
		}
	}

	return result;
};

/**
 * Highlights Mustache template expressions in the command text
 * @param code - Command text to highlight
 * @returns HTML string with highlighted Mustache templates
 */
const createHighlighter = (highlightOutside: boolean, customPrefixes?: PrefixHighlight[]) => {
	/**
	 * Highlights Mustache template expressions in the command text
	 * and, optionally, variable patterns outside of mustache brackets
	 * @param code - Command text to highlight
	 * @returns HTML string with highlighted content
	 */
	return (code: string): string => {
		let result = code
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;');

		// Optionally highlight variables across the whole string first
		if (highlightOutside) {
			result = highlightVariables(result, customPrefixes);
		}

		// Handle unescaped mustache templates {{{ }}}
		result = result.replace(/(\{\{\{)([^{}]+)(\}\}\})/g, (match, openBrackets, content, closeBrackets) => {
			const highlightedContent = highlightOutside ? content : highlightVariables(content, customPrefixes);
			return `<span class="mustache-bracket">${openBrackets}</span><span class="mustache-content-unescaped">${highlightedContent}</span><span class="mustache-bracket">${closeBrackets}</span>`;
		});

		// Handle regular mustache templates {{ }}
		result = result.replace(/(\{\{)([^{}]+)(\}\})/g, (match, openBrackets, content, closeBrackets) => {
			const highlightedContent = highlightOutside ? content : highlightVariables(content, customPrefixes);
			return `<span class="mustache-bracket">${openBrackets}</span><span class="mustache-content">${highlightedContent}</span><span class="mustache-bracket">${closeBrackets}</span>`;
		});

		return result;
	};
};

/**
 * A reusable code editor component with syntax highlighting for Mustache templates and variable patterns
 * Highlights:
 * - Mustache templates: {{ }} and {{{ }}}
 * - Variable patterns: $var.property, $var[property], $secrets.property
 * @param props - Component props
 * @returns TemplateEditor component
 */
const TemplateEditor = (props: TemplateEditorProps): React.JSX.Element => {
	const { value, onChange, placeholder, disabled, className, padding = 10, highlightVariablesOutsideBraces = false, customPrefixes, onBlur } = props;

	return (
		<div className={`${styles.TemplateEditorContainer} ${className || ''}`}>
			<Editor
				value={value}
				onValueChange={onChange}
				onBlur={onBlur}
				highlight={createHighlighter(highlightVariablesOutsideBraces, customPrefixes)}
				padding={padding}
				placeholder={placeholder}
				disabled={disabled}
				textareaClassName={styles.EditorTextarea}
			/>
		</div>
	);
};

export default TemplateEditor;

